import React, { useState } from 'react';
import { Loader2, Target, TrendingUp, CheckCircle, Send, ArrowRight, ChevronDown, ChevronUp, Search, Users, ExternalLink, Database, Wand2, AlertTriangle, Building2, Shield, Info, SearchCheck } from 'lucide-react';
import { WatchlistButton } from '../WatchlistButton';
import { PRIORITY_STYLES, STATUS_STYLES } from './constants';
import { API_URL } from './index';

const TIER_TAGS = {
  mid_market: { label: 'Mid-Market Target', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  growth: { label: 'Growth Stage', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  enterprise: { label: 'Enterprise (Low Priority)', cls: 'bg-orange-100 text-orange-700 border-orange-300' },
  large: { label: 'Large Company', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  excluded: { label: 'Excluded', cls: 'bg-red-100 text-red-700 border-red-300' },
  size_unknown: { label: 'Size Unknown', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const REASON_LABELS = {
  enterprise_known_company: 'Known enterprise company',
  enterprise_pattern_match: 'Matches enterprise pattern',
  employee_count_over_5000: 'Employee count over 5,000',
  internal_ta_likely: 'Likely has internal recruiting team',
  staffing_company_excluded: 'Staffing/recruiting company',
  retail_employer_excluded: 'Large retail employer',
  icp_mid_market_fit: 'Ideal mid-market size (50-1,000)',
  icp_growth_stage: 'Growth stage (1,000-3,000)',
  industry_icp_match: 'Industry matches ICP',
  manual_override: 'Manually overridden by user',
  default_target: 'Default target fit',
  confirmed_target: 'Confirmed target (employee data)',
  no_employee_data: 'No employee count data available',
  no_industry_signal: 'No ICP industry signal',
  unverified_company: 'Company not yet verified',
  known_defense_contractor: 'Known defense contractor',
  clearance_keyword_match: 'Clearance keywords detected',
  repeated_cleared_roles: 'Repeated clearance-heavy postings',
  dod_indicator: 'DoD indicator present',
  government_contract_pattern: 'Government contract pattern',
  defense_industry_match: 'Defense/aerospace industry match',
  confirmed_high_fit: 'Confirmed best-immediate prospect',
  confirmed_mid_market: 'Confirmed mid-market size',
  growth_stage_moderate: 'Growth stage company',
  large_moderate: 'Large but workable',
  large_workforce: 'Large workforce',
  large_company: 'Large company (3,000-5,000 employees)',
  fortune_500_match: 'Fortune 500 company',
};

const CLS_BADGE = {
  high_fit: { label: 'Best Immediate', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  moderate_fit: { label: 'Broader Qualified', cls: 'bg-gray-100 text-gray-700 border-gray-300' },
  stretch_risk: { label: 'Needs Enrichment', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  target: { label: 'Target', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  non_target: { label: 'Strategic Enterprise', cls: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  excluded: { label: 'Excluded', cls: 'bg-red-100 text-red-700 border-red-300' },
  needs_review: { label: 'Needs Review', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
};

const ClassifyPreview = () => {
  const [query, setQuery] = useState('');
  const [empCount, setEmpCount] = useState('');
  const [industry, setIndustry] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleClassify = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('backoffice_token');
      const res = await fetch(`${API_URL}/api/intelligence/acquisition/classify-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          company_name: query.trim(),
          employee_count: empCount ? parseInt(empCount) : 0,
          industry: industry || '',
        }),
      });
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ error: 'Classification failed' });
    }
    setLoading(false);
  };

  const badge = result && !result.error ? CLS_BADGE[result.classification] || CLS_BADGE.target : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4" data-testid="classify-preview">
      <div className="flex items-center gap-2 mb-3">
        <SearchCheck className="h-4 w-4 text-[#D4AF37]" />
        <span className="text-xs font-bold text-[#0A1F44]">Company Classification Preview</span>
        <span className="text-[10px] text-gray-400">Test any company name against the ICP classifier</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Type any company name..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleClassify()}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
          data-testid="classify-input"
        />
        <button
          onClick={() => setShowAdvanced(s => !s)}
          className="px-2 py-2 text-[10px] text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50"
          data-testid="classify-advanced-toggle"
          title="Show employee count & industry inputs"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
        </button>
        <button
          onClick={handleClassify}
          disabled={loading || !query.trim()}
          className="px-4 py-2 bg-[#0A1F44] text-white text-xs font-semibold rounded-lg hover:bg-[#0d2a5c] disabled:opacity-50 flex items-center gap-1.5"
          data-testid="classify-btn"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SearchCheck className="h-3.5 w-3.5" />}
          Classify
        </button>
      </div>

      {showAdvanced && (
        <div className="flex items-center gap-2 mt-2" data-testid="classify-advanced-fields">
          <input type="number" placeholder="Employee count" value={empCount}
            onChange={e => setEmpCount(e.target.value)}
            className="w-36 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50"
            data-testid="classify-employee-count" />
          <input type="text" placeholder="Industry (optional)" value={industry}
            onChange={e => setIndustry(e.target.value)}
            className="w-48 px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/50"
            data-testid="classify-industry" />
        </div>
      )}

      {result && !result.error && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid="classify-result">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-2 py-1 text-xs font-bold rounded border ${badge.cls}`} data-testid="classify-result-badge">
              {badge.label}
            </span>
            <span className="text-xs text-gray-600" data-testid="classify-result-company">
              {result.input?.company_name}
              {result.input?.normalized !== result.input?.company_name?.toLowerCase() &&
                <span className="text-gray-400 ml-1">(normalized: {result.input?.normalized})</span>}
            </span>
            {result.input?.parent_entity !== result.input?.normalized && (
              <span className="text-[10px] text-purple-500 bg-purple-50 px-1.5 py-0.5 rounded">
                parent: {result.input?.parent_entity}
              </span>
            )}
            {result.in_pipeline && (
              <span className="text-[10px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded" data-testid="classify-in-pipeline">
                In Pipeline
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] text-gray-400">Rule:</span>
            <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-600" data-testid="classify-result-rule">
              {result.matched_rule}
            </code>
            {result.matched_value && (
              <>
                <span className="text-[10px] text-gray-400">Matched:</span>
                <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-600" data-testid="classify-result-value">
                  {result.matched_value}
                </code>
              </>
            )}
            <span className="text-[10px] text-gray-400">Tier:</span>
            <span className="text-[10px] font-medium text-gray-600">{result.company_tier}</span>
            <span className="text-[10px] text-gray-400">ICP adj:</span>
            <span className={`text-[10px] font-bold ${result.icp_score_adjustment > 0 ? 'text-emerald-600' : result.icp_score_adjustment < 0 ? 'text-red-500' : 'text-gray-500'}`}>
              {result.icp_score_adjustment > 0 ? '+' : ''}{result.icp_score_adjustment}
            </span>
            {result.classification_confidence && (
              <>
                <span className="text-[10px] text-gray-400">Confidence:</span>
                <span className={`text-[10px] font-bold ${
                  result.classification_confidence === 'high' ? 'text-emerald-600' :
                  result.classification_confidence === 'medium' ? 'text-blue-500' : 'text-amber-500'
                }`} data-testid="classify-result-confidence">
                  {result.classification_confidence}
                </span>
              </>
            )}
          </div>
          {result.reason_codes?.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1 flex-wrap">
              <span className="text-[10px] text-gray-400">Reasons:</span>
              {result.reason_codes.map((r, i) => (
                <span key={i} className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">
                  {REASON_LABELS[r] || r}
                </span>
              ))}
            </div>
          )}
          <div className="mt-1 text-[9px] text-gray-300">
            v{result.classifier_version}
          </div>
        </div>
      )}
      {result?.error && (
        <div className="mt-2 text-xs text-red-500" data-testid="classify-error">{result.error}</div>
      )}
    </div>
  );
};

export const PipelineTab = ({
  companies,
  nonTargetCompanies = [],
  excludedCompanies = [],
  counts = {},
  searchTerm, setSearchTerm,
  filterPriority, setFilterPriority,
  selectedCompanies, setSelectedCompanies, toggleCompanySelection,
  expandedRow, setExpandedRow,
  contacts, loadContacts,
  selectedCampaign,
  pushingLeads, onPushLeads,
  addingCompany, onAddCompanyToCampaign,
  onPromote,
  onOverride,
  detectingRoles, onDetectRoles,
  aiGenerating, onAiPersonalize,
  setActiveTab,
  showNonTarget, setShowNonTarget,
  showExcluded, setShowExcluded,
  showStretch, setShowStretch,
}) => {
  const filtered = companies.filter(c => {
    if (filterPriority !== 'all' && c.auto_priority !== filterPriority) return false;
    if (searchTerm && !c.company.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <ClassifyPreview />
      <div className="flex items-center gap-3" data-testid="pipeline-filters">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search companies..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50"
            data-testid="pipeline-search" />
        </div>
        {['all', 'high', 'medium', 'standard'].map(p => (
          <button key={p} onClick={() => setFilterPriority(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterPriority === p ? 'bg-[#D4AF37] text-[#0A1F44] shadow-sm' : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'}`}
            data-testid={`filter-${p}`}>
            {p === 'all' ? 'All' : p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-2">{filtered.length} companies</span>
        {selectedCompanies.size > 0 && selectedCampaign && (
          <button onClick={onPushLeads} disabled={pushingLeads}
            className="ml-auto px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="push-selected-btn">
            {pushingLeads ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
            Push {selectedCompanies.size} to Campaign
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="pipeline-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-500 text-[11px] uppercase tracking-wider">
              <th className="px-3 py-3 text-center w-8">
                <input type="checkbox" className="rounded border-gray-300"
                  onChange={e => {
                    if (e.target.checked) setSelectedCompanies(new Set(filtered.map(c => c.company)));
                    else setSelectedCompanies(new Set());
                  }}
                  checked={selectedCompanies.size > 0 && selectedCompanies.size === filtered.length}
                  data-testid="select-all-checkbox" />
              </th>
              <th className="px-2 py-3 text-left w-8"></th>
              <th className="px-4 py-3 text-left">Company</th>
              <th className="px-3 py-3 text-center">Score</th>
              <th className="px-3 py-3 text-center">Priority</th>
              <th className="px-3 py-3 text-center">30d</th>
              <th className="px-3 py-3 text-center">7d</th>
              <th className="px-3 py-3 text-center">Contacts</th>
              <th className="px-3 py-3 text-center">Status</th>
              <th className="px-3 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 100).map((item, i) => {
              const isExpanded = expandedRow === i;
              const statusInfo = STATUS_STYLES[item.outreach_status] || STATUS_STYLES[item.status] || STATUS_STYLES.auto_qualified;
              const StatusIcon = statusInfo.icon;
              const compContacts = contacts[item.company] || [];
              const isChecked = selectedCompanies.has(item.company);

              return (
                <React.Fragment key={item.company}>
                  <tr className={`border-t border-gray-100 hover:bg-gray-50/50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/30' : ''} ${isChecked ? 'bg-indigo-50/30' : ''}`}
                    onClick={() => { setExpandedRow(isExpanded ? null : i); if (!isExpanded) loadContacts(item.company); }}
                    data-testid={`pipeline-row-${i}`}>
                    <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" className="rounded border-gray-300"
                        checked={isChecked}
                        onChange={() => toggleCompanySelection(item.company)}
                        data-testid={`select-${i}`} />
                    </td>
                    <td className="px-2 py-3"><WatchlistButton company={item.company} sourceModule="acquisition" /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-[#0A1F44]">{item.company}</span>
                        {item.is_overridden && (
                          <span className="px-1 py-0.5 text-[7px] font-bold rounded bg-purple-100 text-purple-700 border border-purple-300" data-testid={`tag-override-${i}`}>Manual Override</span>
                        )}
                        {(() => {
                          const tier = item.company_tier || 'size_unknown';
                          const tag = TIER_TAGS[tier];
                          return tag ? (
                            <span className={`px-1 py-0.5 text-[7px] font-bold rounded border ${tag.cls}`} data-testid={`tier-tag-${i}`}>{tag.label}</span>
                          ) : null;
                        })()}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                        {item.industry}
                        {item.classification_reasons?.length > 0 && (
                          <span className="text-[9px] text-gray-300" title={item.classification_reasons.map(r => REASON_LABELS[r] || r).join(', ')}>
                            ({item.classification_reasons.map(r => REASON_LABELS[r] || r).join(', ')})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#0A1F44] text-[#D4AF37] font-bold text-xs">{item.auto_score}</span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${PRIORITY_STYLES[item.auto_priority] || PRIORITY_STYLES.standard}`}>
                        {item.auto_priority}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center font-medium text-[#0A1F44]">{item.count_30d || '\u2014'}</td>
                    <td className="px-3 py-3 text-center font-medium text-[#0A1F44]">{item.count_7d || '\u2014'}</td>
                    <td className="px-3 py-3 text-center">
                      {item.has_contacts ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                          <CheckCircle className="h-3 w-3" /> {item.decision_makers_count}
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">Pending</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${statusInfo.cls}`}>
                        <StatusIcon className="h-3 w-3" /> {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-center">
                        {item.target_classification === 'excluded' && !item.is_overridden ? (
                          <span className="text-[9px] text-red-400 font-medium">Excluded</span>
                        ) : item.status !== 'added_to_campaign' && item.outreach_status !== 'added_to_campaign' && (
                          <>
                            {item.status === 'auto_qualified' && (
                              <button onClick={() => onPromote(item.company)}
                                className="px-2 py-1 bg-[#D4AF37] text-[#0A1F44] text-[10px] font-semibold rounded hover:bg-[#c5a030] flex items-center gap-0.5"
                                data-testid={`promote-${i}`}>
                                <ArrowRight className="h-2.5 w-2.5" /> Promote
                              </button>
                            )}
                            {selectedCampaign && item.has_contacts && (
                              <button onClick={() => onAddCompanyToCampaign(item.company)}
                                disabled={addingCompany === item.company}
                                className="px-2 py-1 bg-indigo-600 text-white text-[10px] font-semibold rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-0.5"
                                data-testid={`add-to-campaign-${i}`}>
                                {addingCompany === item.company ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Send className="h-2.5 w-2.5" />}
                                Send
                              </button>
                            )}
                          </>
                        )}
                        {(item.outreach_status === 'added_to_campaign') && (
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-medium rounded">Sent</span>
                        )}
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="bg-gray-50/50">
                      <td colSpan={10} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Signals */}
                          <div>
                            <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Qualification Signals</h4>
                            <div className="space-y-1">
                              {(item.auto_signals || []).map((s, si) => (
                                <div key={si} className="flex items-center gap-2 text-xs">
                                  <TrendingUp className="h-3 w-3 text-[#D4AF37]" />
                                  <span className="text-gray-700">{s}</span>
                                </div>
                              ))}
                            </div>
                            {(item.departments || []).length > 0 && (
                              <div className="mt-3">
                                <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Departments</h4>
                                <div className="flex flex-wrap gap-1">
                                  {item.departments.slice(0, 8).map((d, di) => (
                                    <span key={di} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-[10px]">{d}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Decision Makers */}
                          <div>
                            <h4 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
                              Decision Makers {compContacts.length > 0 && `(${compContacts.length})`}
                            </h4>
                            {compContacts.length === 0 ? (
                              <p className="text-xs text-gray-400">Not yet enriched — click "Enrich Contacts" above</p>
                            ) : (
                              <div className="space-y-2">
                                {compContacts.slice(0, 5).map((c, ci) => (
                                  <div key={ci} className="flex items-center gap-3 p-2 bg-white border border-gray-200 rounded-lg">
                                    <div className="h-8 w-8 bg-[#0A1F44] rounded-full flex items-center justify-center flex-shrink-0">
                                      <span className="text-[#D4AF37] text-[10px] font-bold">{(c.name || '?')[0]}</span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-xs font-medium text-[#0A1F44] truncate">{c.name}</div>
                                      <div className="text-[10px] text-gray-500 truncate">{c.title}</div>
                                      {c.email && <div className="text-[10px] text-blue-600 truncate">{c.email}</div>}
                                      {c.linkedin_url && (
                                        <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-0.5">
                                          <ExternalLink className="h-2.5 w-2.5" /> LinkedIn
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* AI Personalize Button */}
                            <button
                              onClick={() => onAiPersonalize(item.company)}
                              disabled={aiGenerating === item.company}
                              className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#0A1F44] to-[#1a3a6b] text-white text-xs font-semibold rounded-lg hover:from-[#0d2a5c] hover:to-[#1f4480] disabled:opacity-50 transition-all"
                              data-testid={`ai-personalize-${i}`}>
                              {aiGenerating === item.company ? (
                                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating AI Sequence...</>
                              ) : (
                                <><Wand2 className="h-3.5 w-3.5" /> AI Personalize Outreach</>
                              )}
                            </button>
                            <button
                              onClick={() => { onDetectRoles(item.company); setActiveTab('roles'); }}
                              disabled={detectingRoles === item.company}
                              className="mt-1.5 w-full flex items-center justify-center gap-1.5 px-3 py-2 border border-[#0A1F44]/20 text-[#0A1F44] text-xs font-semibold rounded-lg hover:bg-[#0A1F44]/5 disabled:opacity-50 transition-all"
                              data-testid={`detect-roles-${i}`}>
                              {detectingRoles === item.company ? (
                                <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Detecting Roles...</>
                              ) : (
                                <><Users className="h-3.5 w-3.5" /> Build Candidate Pipeline</>
                              )}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-12 text-center" data-testid="pipeline-empty">
            <Target className="h-10 w-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">No companies match your filters</p>
          </div>
        )}
      </div>

      {/* Needs Enrichment Section (companies with insufficient data) */}
      {(counts.stretch_risk > 0) && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="stretch-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-500" />
              <span className="text-xs font-medium text-amber-700">
                {counts.stretch_risk || 0} Needs Enrichment
              </span>
              <span className="text-[10px] text-amber-400">(insufficient data, unverified)</span>
            </div>
            <button onClick={() => setShowStretch && setShowStretch(s => !s)}
              className="px-3 py-1 text-[10px] font-medium text-amber-600 border border-amber-300 rounded hover:bg-amber-100"
              data-testid="toggle-stretch-btn">
              {showStretch ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
      )}

      {/* Strategic Enterprise Section */}
      {(counts.non_target > 0 || nonTargetCompanies.length > 0) && (
        <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="non-target-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-400" />
              <span className="text-xs font-medium text-indigo-700">
                {counts.non_target || 0} Strategic Enterprise
              </span>
              <span className="text-[10px] text-indigo-400">(large enterprise, hidden by default)</span>
            </div>
            <button onClick={() => setShowNonTarget && setShowNonTarget(s => !s)}
              className="px-3 py-1 text-[10px] font-medium text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-100"
              data-testid="toggle-non-target-btn">
              {showNonTarget ? 'Hide' : 'Show'}
            </button>
          </div>
          {showNonTarget && nonTargetCompanies.length > 0 && (
            <div className="mt-2 space-y-1">
              {nonTargetCompanies.map((item, i) => {
                const tier = item.company_tier || 'enterprise';
                const tag = TIER_TAGS[tier] || TIER_TAGS.enterprise;
                return (
                  <div key={item.company} className="flex items-center justify-between p-2 bg-white rounded border border-orange-100" data-testid={`non-target-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#0A1F44]">{item.company}</span>
                      <span className={`px-1 py-0.5 text-[7px] font-bold rounded border ${tag.cls}`}>{tag.label}</span>
                      {item.classification_reasons?.length > 0 && (
                        <span className="text-[9px] text-gray-400" title={item.classification_reasons.map(r => REASON_LABELS[r] || r).join(', ')}>
                          {item.classification_reasons.map(r => REASON_LABELS[r] || r).join(', ')}
                        </span>
                      )}
                    </div>
                    {onOverride && (
                      <button onClick={() => onOverride(item.company)}
                        className="px-2 py-1 text-[9px] font-medium text-purple-600 border border-purple-300 rounded hover:bg-purple-50"
                        data-testid={`override-${item.company}`}>
                        <Shield className="h-2.5 w-2.5 inline mr-0.5" /> Override
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Excluded Section (collapsed by default) */}
      {(counts.excluded > 0 || excludedCompanies.length > 0) && (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="excluded-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-600">
                {counts.excluded || 0} Excluded
              </span>
              <span className="text-[10px] text-gray-400">(staffing, retail, defense/clearance)</span>
            </div>
            <button onClick={() => setShowExcluded && setShowExcluded(s => !s)}
              className="px-3 py-1 text-[10px] font-medium text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
              data-testid="toggle-excluded-btn">
              {showExcluded ? 'Hide' : 'Show'}
            </button>
          </div>
          {showExcluded && excludedCompanies.length > 0 && (
            <div className="mt-2 space-y-1">
              {excludedCompanies.map((item, i) => (
                <div key={item.company} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100" data-testid={`excluded-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-500">{item.company}</span>
                    <span className="px-1 py-0.5 text-[7px] font-bold rounded bg-red-100 text-red-700 border border-red-300">Excluded</span>
                    {item.classification_reasons?.length > 0 && (
                      <span className="text-[9px] text-gray-400">
                        {item.classification_reasons.map(r => REASON_LABELS[r] || r).join(', ')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};
